import { Image } from '@chakra-ui/react';
import { getAgent } from '@cronos-app/api';
import type { ExplicitAddress } from '@cronos-app/sdk';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { TokenConfig } from '@ui/config/token';
import { Token } from '@ui/config/token';
import { useSDK } from '@ui/utils';

import { useGlobalChainLayer } from '../useGlobalChainLayer';

interface UseAgentTokenConfigOptions {
  tokenAddress: ExplicitAddress | undefined;
  enabled?: boolean;
}

export const useAgentTokenConfig = ({
  tokenAddress,
  enabled,
}: UseAgentTokenConfigOptions): UseQueryResult<TokenConfig> => {
  const chainLayer = useGlobalChainLayer();
  const sdk = useSDK();

  return useQuery({
    queryKey: ['useAgentTokenConfig', tokenAddress, sdk, chainLayer],
    queryFn: async () => {
      if (!tokenAddress) throw new Error('tokenAddress is empty');

      const erc20 = sdk.getERC20(tokenAddress);
      const [symbol, decimals, name, agentInfo] = await Promise.all([
        erc20.symbol(),
        erc20.decimals(),
        erc20.name(),
        getAgent(chainLayer, tokenAddress),
      ]);
      const tokenInfo = { symbol, decimals, name, image: agentInfo.image_url };

      const AgentTokenConfig: TokenConfig = {
        type: Token.Agent,
        symbol: tokenInfo.symbol,
        address: tokenAddress,
        decimals: Number(tokenInfo.decimals),
        displayDecimals: 6,
        Icon: (props) => {
          return (
            tokenInfo.image && (
              <Image src={tokenInfo.image} alt={name} {...props} />
            )
          );
        },
      };
      return AgentTokenConfig;
    },
    staleTime: Infinity,
    enabled: enabled === false ? false : !!tokenAddress && !!sdk,
  });
};
